import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploader
 * -------------
 * Drop-in replacement for a text "Image URLs" input. Accepts drag-drop or click-to-pick,
 * downscales every image to <= 1200px width (JPEG q=0.82) so payloads stay small enough
 * to embed as base64 data URLs directly in MongoDB (no S3 needed).
 *
 * Value shape: comma-separated string of data-URLs / http URLs (so the existing PostListing
 * submit path — which splits on "," and passes to backend `images` array — keeps working
 * unchanged).
 */
const MAX_WIDTH = 1200;
const MAX_IMAGES_DEFAULT = 6;
const JPEG_QUALITY = 0.82;

function fileToDownscaledDataUrl(file, maxWidth = MAX_WIDTH) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Not an image file"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Image decode failed"));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ImageUploader({
  value = "",
  onChange,
  maxImages = MAX_IMAGES_DEFAULT,
  circular = false,
  helperText,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const images = value
    ? value.split("\n").map(s => s.trim()).filter(Boolean)
    : [];

  const commit = (arr) => {
    onChange(arr.join("\n"));
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, Math.max(0, maxImages - images.length));
    if (files.length === 0) {
      toast.error(`You can upload up to ${maxImages} images.`);
      return;
    }
    setBusy(true);
    try {
      const results = await Promise.all(files.map(f => fileToDownscaledDataUrl(f).catch(() => null)));
      const kept = results.filter(Boolean);
      if (kept.length === 0) {
        toast.error("Nothing to upload — please pick valid image files.");
      } else {
        commit([...images, ...kept]);
        toast.success(`${kept.length} image${kept.length > 1 ? "s" : ""} added.`);
      }
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (i) => {
    const next = images.slice();
    next.splice(i, 1);
    commit(next);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-5 cursor-pointer transition-colors ${
          dragOver
            ? "border-[#E05E36] bg-[#FFF3EC]"
            : "border-stone-300 bg-stone-50 hover:border-stone-400"
        }`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        data-testid="image-uploader-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="image-uploader-input"
        />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white border border-stone-300 grid place-items-center text-stone-600 shrink-0">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-stone-800">
              {busy ? "Processing…" : "Drop images here or click to browse"}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              {helperText || `JPG / PNG · Up to ${maxImages} · auto-resized to 1200px`}
            </div>
          </div>
        </div>
      </div>

      {images.length > 0 && (
        <div className={`mt-3 grid gap-2 ${circular ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-4"}`} data-testid="image-uploader-previews">
          {images.map((src, i) => (
            <div key={i} className="relative group">
              <img
                src={src}
                alt={`upload ${i + 1}`}
                className={`w-full ${circular ? "h-16 rounded-full" : "h-24 rounded-xl"} object-cover border border-stone-200`}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-stone-300 shadow grid place-items-center text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
                data-testid={`image-uploader-remove-${i}`}
              >
                <X size={12} />
              </button>
              {i === 0 && circular && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-widest bg-[#2D5A27] text-white px-1.5 rounded-full">
                  <Camera size={8} className="inline -mt-0.5" /> Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
